// src/components/RestaurantPOS/cashier/eod/EODReport.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Fade,
  useTheme,
  alpha,
  Tooltip,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  PhoneIphone as MobileIcon,
  CardGiftcard as ComplimentaryIcon,
  Restaurant as RestaurantIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  TableRestaurant as TableRestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  ReceiptLong as ReceiptLongIcon,
  Discount as DiscountIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import restaurantConfig from '../../../config/Organisation_Details';

// ============================================
// CONSTANTS
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

// Payment method mapping
const PAYMENT_METHOD_NAMES = {
  1: 'Cash',
  2: 'Card',
  3: 'Bank Transfer',
  4: 'Mobile Money',
  5: 'Complimentary',
};

const PAYMENT_METHOD_COLORS = {
  'Cash': '#4CAF50',
  'Card': '#1976D2',
  'Bank Transfer': '#9C27B0',
  'Mobile Money': '#FF6F00',
  'Complimentary': '#FF9800',
};

// ✅ ADD: Payment status colors for status chips
const PAYMENT_STATUS_COLORS = {
  UNPAID: { bg: '#FFEBEE', color: '#C62828' },
  PAID: { bg: '#E8F5E9', color: '#2E7D32' },
  PARTIAL: { bg: '#FFF3E0', color: '#E65100' },
  REFUNDED: { bg: '#F3E5F5', color: '#4A148C' },
  COMPLIMENTARY: { bg: '#F3E5F5', color: '#7B1FA2' },
  CANCELLED: { bg: '#FFEBEE', color: '#C62828' },
};

// ✅ ADD: Order status colors
const ORDER_STATUS_COLORS = {
  PENDING: { bg: '#FFF3E0', color: '#E65100' },
  IN_PROGRESS: { bg: '#E3F2FD', color: '#0D47A1' },
  READY: { bg: '#E8F5E9', color: '#1B5E20' },
  COMPLETED: { bg: '#F3E5F5', color: '#4A148C' },
  CANCELLED: { bg: '#FFEBEE', color: '#B71C1C' },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatTime = (timeString) => {
  if (!timeString) return 'N/A';
  const date = new Date(timeString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const formatCompactCurrency = (amount, currency = '₦') => {
  if (amount === null || amount === undefined) return `${currency}0.00`;
  // Always show 2 decimal places for amounts under 1000
  if (amount < 1000) {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
  if (amount >= 1000000) {
    return `${currency}${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `${currency}${(amount / 1000).toFixed(1)}K`;
  }
  return `${currency}${Number(amount).toFixed(2)}`;
};

// ✅ ADD: Get status chip function for consistent status display
const getStatusChip = (status, type = 'payment') => {
  if (!status) return <Chip size="small" label="Unknown" />;
  
  const colors = type === 'payment' ? PAYMENT_STATUS_COLORS[status] : ORDER_STATUS_COLORS[status];
  if (!colors) {
    return <Chip size="small" label={status.replace('_', ' ')} />;
  }
  
  const label = status.replace('_', ' ');
  
  let chipSx = {
    bgcolor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '0.6rem',
    height: 22,
    '& .MuiChip-label': {
      px: 1,
    },
  };
  
  // Special styling for special statuses
  if (status === 'COMPLIMENTARY') {
    chipSx = { ...chipSx, border: '1px solid #9C27B0' };
  }
  if (status === 'CANCELLED') {
    chipSx = { ...chipSx, border: '1px solid #F44336' };
  }
  if (status === 'PAID') {
    chipSx = { ...chipSx, border: '1px solid #4CAF50' };
  }
  if (status === 'REFUNDED') {
    chipSx = { ...chipSx, border: '1px solid #9C27B0' };
  }
  
  return <Chip size="small" label={label} sx={chipSx} />;
};

// ============================================
// API HELPERS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[EODReport] Error checking token expiry:', err);
    return true;
  }
};

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[EODReport] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[EODReport] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[EODReport] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[EODReport] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[EODReport] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  let url = `${FULL_API_URL}${endpoint}`;
  
  if (options.params) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  console.debug(`[EODReport] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { 
      ...options, 
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined
    });

    if (response.status === 401) {
      console.debug('[EODReport] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[EODReport] API Error:', err.message);
    throw err;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const EODReport = ({
  open,
  onClose,
  locationId,
  locationName,
  closureData,
  onRefresh,
}) => {
  const theme = useTheme();

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const printWindowRef = useRef(null);
  const isMountedRef = useRef(true);

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchReportData = useCallback(async () => {
    if (!locationId) return;

    setIsLoading(true);
    setError(null);

    try {
      console.debug(`[EODReport] Fetching report data for location: ${locationId}`);
      
      // If closureData is provided, use it
      if (closureData) {
        console.debug('[EODReport] Using provided closure data');
        setReportData(closureData);
        setIsLoading(false);
        return;
      }

      // Otherwise fetch from API
      const data = await apiRequest('/eod/report', {
        params: { location_id: locationId }
      });

      console.debug('[EODReport] Report data fetched:', data);
      
      if (isMountedRef.current) {
        setReportData(data);
      }
    } catch (err) {
      console.error('[EODReport] Error fetching report data:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to fetch report data');
        setSnackbar({
          open: true,
          message: `Failed to fetch report: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [locationId, closureData]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;
    
    if (open) {
      fetchReportData();
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [open, fetchReportData]);

  // ============================================
  // PRINT FUNCTIONS - COMPLETE
  // ============================================

  const generateThermalReceipt = useCallback((data) => {
    if (!data) return '';

    const currencySymbol = restaurantConfig.currency || '₦';
    const restaurantName = restaurantConfig.name || 'RESTAURANT';
    const restaurantAddress = restaurantConfig.address || '';
    const restaurantPhone = restaurantConfig.phone || '';

    const formatCurrencyLocal = (amount) => {
      return `${currencySymbol} ${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const formatDateLocal = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };

    const formatTimeLocal = (timeString) => {
      if (!timeString) return 'N/A';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    };

    const closure = data.closure || data;
    const paymentMethods = data.payment_methods || {};
    const orderHistory = data.order_history || [];
    const waiters = data.waiters || [];
    const discounts = data.discounts || { total_discount: 0, orders_with_discount: 0 };
    const complimentary = data.complimentary || { total_count: 0, total_amount: 0 };

    const totalRevenue = closure.total_revenue || 0;
    const totalOrders = closure.total_orders || 0;

    // Build the receipt HTML
    return `
      <div style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:9px;line-height:1.5;color:#000000;font-weight:bold;">
        <!-- Header -->
        <div style="text-align:center;padding-bottom:10px;border-bottom:3px solid #000;margin-bottom:10px;">
          <div style="font-size:18px;font-weight:bold;letter-spacing:2px;color:#000;">${restaurantName}</div>
          ${restaurantAddress ? `<div style="font-size:8px;font-weight:bold;color:#333;">${restaurantAddress}</div>` : ''}
          ${restaurantPhone ? `<div style="font-size:8px;font-weight:bold;color:#333;">Tel: ${restaurantPhone}</div>` : ''}
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:16px;font-weight:bold;letter-spacing:2px;color:#000;">END OF DAY REPORT</div>
          <div style="font-size:9px;font-weight:bold;color:#333;margin-top:3px;">${formatDateLocal(closure.closure_date)}</div>
          <div style="font-size:8px;font-weight:bold;color:#666;">Closed: ${formatTimeLocal(closure.closed_at)}</div>
        </div>

        <!-- Cashier Info -->
        <div style="margin-bottom:8px;padding:4px 2px;background:#f5f5f5;border-radius:4px;">
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
            <span>Location: ${locationName || 'N/A'}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
            <span>Cashier: ${closure.closed_by_cashier_name || 'N/A'}</span>
            <span>ID: #${closure.closed_by_cashier_id || 'N/A'}</span>
          </div>
          ${closure.notes ? `
            <div style="padding:2px 0;font-size:7px;color:#555;font-weight:bold;">
              Note: ${closure.notes}
            </div>
          ` : ''}
        </div>

        <!-- Summary Section -->
        <div style="margin-bottom:8px;border:2px solid #000;padding:4px;">
          <div style="text-align:center;font-size:10px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:2px solid #000;padding-bottom:2px;margin-bottom:4px;">
            📊 SUMMARY
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9px;font-weight:bold;color:#000;">
            <span>Total Orders</span>
            <span>${totalOrders}</span>
          </div>
          <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:11px;font-weight:bold;color:#2E7D32;">
            <span>TOTAL REVENUE</span>
            <span>${formatCurrencyLocal(totalRevenue)}</span>
          </div>
        </div>

        <!-- Payment Methods Breakdown -->
        <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;">
          <div style="text-align:center;font-size:9px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:1px dashed #ccc;padding-bottom:2px;margin-bottom:4px;">
            💳 PAYMENT METHODS
          </div>
          ${Object.entries(paymentMethods).map(([method, amount]) => `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
              <span>${method}</span>
              <span>${formatCurrencyLocal(amount)}</span>
            </div>
          `).join('')}
          ${Object.keys(paymentMethods).length === 0 ? `
            <div style="text-align:center;padding:4px 0;font-size:7px;color:#999;font-weight:bold;">
              No payment records found
            </div>
          ` : ''}
        </div>

        <!-- Discounts Section -->
        ${(discounts.total_discount > 0 || discounts.orders_with_discount > 0) ? `
          <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;">
            <div style="text-align:center;font-size:9px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:1px dashed #ccc;padding-bottom:2px;margin-bottom:4px;">
              🏷️ DISCOUNTS
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
              <span>Total Discount</span>
              <span style="color:#E65100;">-${formatCurrencyLocal(discounts.total_discount)}</span>
            </div>
            ${discounts.orders_with_discount > 0 ? `
              <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
                <span>Orders with Discount</span>
                <span>${discounts.orders_with_discount}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <!-- Complimentary Section -->
        ${(complimentary.total_count > 0 || complimentary.total_amount > 0) ? `
          <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;">
            <div style="text-align:center;font-size:9px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:1px dashed #ccc;padding-bottom:2px;margin-bottom:4px;">
              🎁 COMPLIMENTARY
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
              <span>Total Complimentary Orders</span>
              <span>${complimentary.total_count}</span>
            </div>
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
              <span>Total Complimentary Amount</span>
              <span style="color:#FF6F00;">${formatCurrencyLocal(complimentary.total_amount)}</span>
            </div>
          </div>
        ` : ''}

        <!-- Waiter Performance -->
        ${waiters.length > 0 ? `
          <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;">
            <div style="text-align:center;font-size:9px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:1px dashed #ccc;padding-bottom:2px;margin-bottom:4px;">
              👨‍🍳 WAITER PERFORMANCE
            </div>
            ${waiters.map(waiter => `
              <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#000;">
                <span>${waiter.waiter_name}</span>
                <span>${waiter.order_count} orders • ${formatCurrencyLocal(waiter.total_revenue)}</span>
              </div>
            `).join('')}
          </div>
        ` : ''}

        <!-- Order History -->
        ${orderHistory.length > 0 ? `
          <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;">
            <div style="text-align:center;font-size:9px;font-weight:bold;letter-spacing:1px;color:#000;border-bottom:1px dashed #ccc;padding-bottom:2px;margin-bottom:4px;">
              📋 ORDER HISTORY
            </div>
            <div style="display:grid;grid-template-columns:25px 1.2fr 0.8fr 0.8fr;gap:2px;font-size:6px;font-weight:bold;color:#666;border-bottom:1px dotted #ccc;padding-bottom:2px;margin-bottom:4px;">
              <span>#</span>
              <span>Order</span>
              <span>Payment</span>
              <span style="text-align:right;">Amount</span>
            </div>
            ${orderHistory.slice(0, 20).map((order, index) => {
              let displayText = order.payment_display || order.payment_method || 'N/A';
              const color = PAYMENT_METHOD_COLORS[order.payment_method] || '#666';
              return `
                <div style="display:grid;grid-template-columns:25px 1.2fr 0.8fr 0.8fr;gap:2px;padding:2px 0;font-size:6px;font-weight:bold;color:#000;border-bottom:1px dotted #eee;">
                  <span>${index + 1}</span>
                  <span>${order.order_number}</span>
                  <span style="color:${color};">${displayText}</span>
                  <span style="text-align:right;">${formatCurrencyLocal(order.amount)}</span>
                </div>
              `;
            }).join('')}
            ${orderHistory.length > 20 ? `
              <div style="text-align:center;padding:4px 0;font-size:6px;color:#999;font-weight:bold;">
                + ${orderHistory.length - 20} more orders
              </div>
            ` : ''}
          </div>
        ` : `
          <div style="margin-bottom:8px;border:1px solid #ccc;padding:4px;text-align:center;font-size:7px;color:#999;font-weight:bold;">
            No order history found
          </div>
        `}

        <!-- Totals Summary -->
        <div style="border-top:3px double #000;padding-top:8px;margin-top:8px;">
          <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:11px;font-weight:bold;color:#000;">
            <span>GRAND TOTAL</span>
            <span>${formatCurrencyLocal(totalRevenue)}</span>
          </div>
          ${closure.total_cash > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#4CAF50;">
              <span>Cash</span>
              <span>${formatCurrencyLocal(closure.total_cash)}</span>
            </div>
          ` : ''}
          ${closure.total_card > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#1976D2;">
              <span>Card</span>
              <span>${formatCurrencyLocal(closure.total_card)}</span>
            </div>
          ` : ''}
          ${closure.total_transfer > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#9C27B0;">
              <span>Bank Transfer</span>
              <span>${formatCurrencyLocal(closure.total_transfer)}</span>
            </div>
          ` : ''}
          ${closure.total_mobile_money > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#FF6F00;">
              <span>Mobile Money</span>
              <span>${formatCurrencyLocal(closure.total_mobile_money)}</span>
            </div>
          ` : ''}
          ${closure.total_complimentary > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#FF6F00;">
              <span>Complimentary</span>
              <span>${closure.total_complimentary} (${formatCurrencyLocal(closure.complimentary_amount)})</span>
            </div>
          ` : ''}
          ${discounts.total_discount > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#E65100;">
              <span>Total Discount</span>
              <span>-${formatCurrencyLocal(discounts.total_discount)}</span>
            </div>
          ` : ''}
          ${closure.pending_orders_count > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#C62828;">
              <span>Pending Orders</span>
              <span>${closure.pending_orders_count}</span>
            </div>
          ` : ''}
          ${closure.unpaid_orders_count > 0 ? `
            <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:8px;font-weight:bold;color:#C62828;">
              <span>Unpaid Orders</span>
              <span>${closure.unpaid_orders_count}</span>
            </div>
          ` : ''}
        </div>

        <!-- Footer -->
        <div style="text-align:center;padding-top:10px;border-top:3px solid #000;margin-top:10px;">
          <div style="font-size:10px;font-weight:bold;color:#000;">Thank you for your service!</div>
          <div style="border-top:1px dashed #000;margin:6px 0;"></div>
          <div style="font-size:6px;color:#666;margin-top:2px;font-weight:bold;">
            ${restaurantName} • End of Day Report
          </div>
          <div style="font-size:6px;color:#999;margin-top:2px;font-weight:bold;">
            Generated on ${new Date().toLocaleString()}
          </div>
          <div style="font-size:5px;color:#ccc;margin-top:2px;font-weight:bold;">
            --- END OF REPORT ---
          </div>
        </div>
      </div>
    `;
  }, [locationName]);

  const openPrintWindow = useCallback((htmlContent, title) => {
    if (printWindowRef.current && !printWindowRef.current.closed) {
      printWindowRef.current.close();
    }
    
    const printWindow = window.open('', '_blank', 'width=400,height=600,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
    
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: 'Please allow popups for printing',
        severity: 'warning',
      });
      return null;
    }
    
    printWindowRef.current = printWindow;
    
    const allStyles = document.querySelectorAll('style');
    let combinedStyles = '';
    allStyles.forEach(style => {
      combinedStyles += style.innerHTML;
    });
    
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <style>
            ${combinedStyles}
            @media print {
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { margin: 0; padding: 0; background: white; }
              .no-print { display: none !important; }
              #print-content { max-width: 80mm; margin: 0 auto; padding: 8px 6px; }
            }
            body {
              background: #f0f0f0;
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            #print-content {
              max-width: 80mm;
              margin: 0 auto;
              background: white;
              padding: 10px 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
              border-radius: 4px;
            }
            .print-instructions {
              text-align: center;
              padding: 10px;
              margin-bottom: 10px;
              background: #e3f2fd;
              border-radius: 4px;
              font-family: Arial, sans-serif;
              font-size: 12px;
              max-width: 80mm;
            }
            .print-button {
              display: block;
              width: 100%;
              max-width: 80mm;
              padding: 12px;
              margin-top: 10px;
              background: #1976D2;
              color: white;
              border: none;
              border-radius: 4px;
              font-size: 14px;
              font-weight: bold;
              cursor: pointer;
            }
            .print-button:hover {
              background: #1565C0;
            }
          </style>
        </head>
        <body>
          <div class="print-instructions no-print">
            🖨️ Click the "Print" button below to print
            <br>
            <small>${title}</small>
          </div>
          <div id="print-content">
            ${htmlContent}
          </div>
          <button class="print-button no-print" onclick="window.print(); setTimeout(function() { window.close(); }, 3000);">
            🖨️ Print ${title}
          </button>
          <script>
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 3000);
            }, 500);
          <\/script>
        </body>
      </html>
    `;
    
    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    return printWindow;
  }, []);

  const handlePrint = useCallback(async () => {
    if (!reportData) {
      setSnackbar({
        open: true,
        message: 'No report data to print',
        severity: 'warning',
      });
      return;
    }

    setIsPrinting(true);
    
    try {
      console.debug('[EODReport] Printing report...');
      
      const receiptHTML = generateThermalReceipt(reportData);
      const title = `EOD Report - ${formatDate(reportData.closure?.closure_date || new Date())}`;
      const printWindow = openPrintWindow(receiptHTML, title);
      
      if (printWindow) {
        setSnackbar({
          open: true,
          message: 'Report sent to printer!',
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('[EODReport] Error printing report:', err);
      setSnackbar({
        open: true,
        message: `Failed to print report: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setTimeout(() => {
        setIsPrinting(false);
      }, 1000);
    }
  }, [reportData, generateThermalReceipt, openPrintWindow]);

  // ============================================
  // RENDER FUNCTIONS - COMPLETE
  // ============================================

  const renderPaymentMethods = useCallback(() => {
    if (!reportData) return null;
    
    const closure = reportData.closure || reportData;
    const paymentMethods = reportData.payment_methods || {};
    
    // If we have the payment_methods_summary from the closure, use it
    if (Object.keys(paymentMethods).length > 0) {
      return (
        <Grid container spacing={1.5}>
          {Object.entries(paymentMethods).map(([method, amount]) => {
            const color = PAYMENT_METHOD_COLORS[method] || '#666';
            const icons = {
              'Cash': <MoneyIcon />,
              'Card': <CreditCardIcon />,
              'Bank Transfer': <BankIcon />,
              'Mobile Money': <MobileIcon />,
              'Complimentary': <ComplimentaryIcon />,
            };
            return (
              <Grid item xs={6} sm={4} md={3} key={method}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha(color, 0.15)}`,
                    bgcolor: alpha(color, 0.05),
                    textAlign: 'center',
                    height: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(color, 0.15),
                      color: color,
                      width: { xs: 36, sm: 44 },
                      height: { xs: 36, sm: 44 },
                      mx: 'auto',
                      mb: 1,
                    }}
                  >
                    {icons[method] || <MoneyIcon />}
                  </Avatar>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, display: 'block' }}
                  >
                    {method}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      color: color,
                    }}
                  >
                    {formatCurrency(amount)}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    // Fallback: build from closure data
    const methods = [];
    if (closure.total_cash > 0) methods.push({ label: 'Cash', amount: closure.total_cash });
    if (closure.total_card > 0) methods.push({ label: 'Card', amount: closure.total_card });
    if (closure.total_transfer > 0) methods.push({ label: 'Bank Transfer', amount: closure.total_transfer });
    if (closure.total_mobile_money > 0) methods.push({ label: 'Mobile Money', amount: closure.total_mobile_money });
    if (closure.total_complimentary > 0) methods.push({ label: 'Complimentary', amount: closure.complimentary_amount });

    if (methods.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No payment records found
        </Typography>
      );
    }

    return (
      <Grid container spacing={1.5}>
        {methods.map((method) => {
          const color = PAYMENT_METHOD_COLORS[method.label] || '#666';
          const icons = {
            'Cash': <MoneyIcon />,
            'Card': <CreditCardIcon />,
            'Bank Transfer': <BankIcon />,
            'Mobile Money': <MobileIcon />,
            'Complimentary': <ComplimentaryIcon />,
          };
          return (
            <Grid item xs={6} sm={4} md={3} key={method.label}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha(color, 0.15)}`,
                  bgcolor: alpha(color, 0.05),
                  textAlign: 'center',
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(color, 0.15),
                    color: color,
                    width: { xs: 36, sm: 44 },
                    height: { xs: 36, sm: 44 },
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  {icons[method.label] || <MoneyIcon />}
                </Avatar>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, display: 'block' }}
                >
                  {method.label}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '1rem' },
                    color: color,
                  }}
                >
                  {formatCurrency(method.amount)}
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  }, [reportData, theme]);

  const renderWaiterPerformance = useCallback(() => {
    if (!reportData) return null;
    
    const waiters = reportData.waiters || [];
    
    if (waiters.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No waiter records found
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                <strong>Waiter</strong>
              </TableCell>
              <TableCell align="center" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                <strong>Orders</strong>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                <strong>Revenue</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {waiters.map((waiter) => (
              <TableRow key={waiter.waiter_id} hover>
                <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, fontWeight: 500 }}>
                  {waiter.waiter_name}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={waiter.order_count}
                    color="primary"
                    sx={{
                      fontSize: { xs: '0.5rem', sm: '0.7rem' },
                      height: { xs: 20, sm: 24 },
                      minWidth: { xs: 24, sm: 32 },
                    }}
                  />
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontSize: { xs: '0.55rem', sm: '0.75rem' },
                    fontWeight: 600,
                    color: '#2E7D32',
                  }}
                >
                  {formatCurrency(waiter.total_revenue)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [reportData]);

  const renderDiscounts = useCallback(() => {
    if (!reportData) return null;
    
    const discounts = reportData.discounts || { total_discount: 0, orders_with_discount: 0, discount_breakdown: {} };
    
    if (discounts.total_discount === 0 && discounts.orders_with_discount === 0) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No discounts applied today
        </Typography>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: '12px',
          border: `1px solid ${alpha('#E65100', 0.15)}`,
          bgcolor: alpha('#E65100', 0.05),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DiscountIcon sx={{ color: '#E65100' }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#E65100' }}>
            Discounts Summary
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Discount
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#E65100' }}>
              {formatCurrency(discounts.total_discount)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Orders with Discount
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {discounts.orders_with_discount}
            </Typography>
          </Grid>
        </Grid>
        {discounts.discount_breakdown && Object.keys(discounts.discount_breakdown).length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Breakdown by Order
            </Typography>
            {Object.entries(discounts.discount_breakdown).map(([order, amount]) => (
              <Box key={order} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: `1px solid ${alpha('#E65100', 0.1)}` }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{order}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#E65100', fontSize: '0.75rem' }}>
                  -{formatCurrency(amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    );
  }, [reportData]);

  const renderComplimentary = useCallback(() => {
    if (!reportData) return null;
    
    const complimentary = reportData.complimentary || { total_count: 0, total_amount: 0, orders: [] };
    
    if (complimentary.total_count === 0 && complimentary.total_amount === 0) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
          No complimentary orders today
        </Typography>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: '12px',
          border: `1px solid ${alpha('#FF9800', 0.15)}`,
          bgcolor: alpha('#FF9800', 0.05),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ComplimentaryIcon sx={{ color: '#FF9800' }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#FF9800' }}>
            Complimentary Orders
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Complimentary Orders
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#FF9800' }}>
              {complimentary.total_count}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Amount
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#FF9800' }}>
              {formatCurrency(complimentary.total_amount)}
            </Typography>
          </Grid>
        </Grid>
        {complimentary.orders && complimentary.orders.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Orders
            </Typography>
            {complimentary.orders.map((order) => (
              <Box key={order.order_id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: `1px solid ${alpha('#FF9800', 0.1)}` }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>{order.order_number}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#FF9800', fontSize: '0.75rem' }}>
                  {formatCurrency(order.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    );
  }, [reportData]);

  // ✅ FIXED: renderOrderHistory - Now properly displays all orders with statuses
  const renderOrderHistory = useCallback(() => {
    if (!reportData) return null;
    
    const orderHistory = reportData.order_history || [];
    
    if (orderHistory.length === 0) {
      return (
        <Box sx={{ py: 2 }}>
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            No order history found for this day.
            {reportData.closure?.is_auto_closed && (
              <Box component="span" display="block" mt={1}>
                <strong>Note:</strong> This was an auto-closed day with no activity.
              </Box>
            )}
          </Alert>
        </Box>
      );
    }

    const displayOrders = orderHistory.slice(0, 20);
    const hasMore = orderHistory.length > 20;

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '6%' }}>
                <strong>#</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '16%' }}>
                <strong>Order #</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '14%' }}>
                <strong>Order Status</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '14%' }}>
                <strong>Payment Status</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '18%' }}>
                <strong>Payment Method</strong>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '12%' }}>
                <strong>Amount</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, width: '14%' }}>
                <strong>Waiter</strong>
              </TableCell>
              <TableCell sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, width: '12%' }}>
                <strong>Time</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayOrders.map((order, index) => {
              // Determine the payment method
              let primaryMethod = order.payment_method || 'Unknown';
              let displayText = order.payment_display || primaryMethod;
              let isSplit = order.payment_type === 'Split' && order.split_details;
              
              // Get color for the primary method
              const color = PAYMENT_METHOD_COLORS[primaryMethod] || '#666';
              
              // Determine if order has special status
              const isComplimentary = order.payment_status === 'COMPLIMENTARY';
              const isCancelled = order.payment_status === 'CANCELLED';
              const isRefunded = order.payment_status === 'REFUNDED';
              const isPaid = order.payment_status === 'PAID';
              
              // Row background for special statuses
              const rowBg = isComplimentary ? alpha('#9C27B0', 0.05) :
                            isCancelled ? alpha('#F44336', 0.05) :
                            isRefunded ? alpha('#9C27B0', 0.05) :
                            isPaid ? alpha('#4CAF50', 0.05) : 'inherit';
              
              return (
                <TableRow key={order.order_id || index} hover sx={{ bgcolor: rowBg }}>
                  <TableCell sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, color: 'text.secondary' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, fontWeight: 500 }}>
                    {order.order_number}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(order.order_status, 'order')}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(order.payment_status, 'payment')}
                  </TableCell>
                  <TableCell>
                    {isSplit ? (
                      <Tooltip 
                        title={
                          <Box sx={{ p: 1 }}>
                            <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                              Split Payment Breakdown:
                            </Typography>
                            {Object.entries(order.split_details).map(([method, amount]) => (
                              <Typography key={method} variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                {method}: ₦{Number(amount).toFixed(2)}
                              </Typography>
                            ))}
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                              Total: ₦{Number(order.amount).toFixed(2)}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Chip
                          size="small"
                          label={displayText}
                          sx={{
                            fontSize: { xs: '0.45rem', sm: '0.65rem' },
                            height: { xs: 18, sm: 22 },
                            bgcolor: alpha('#FF6F00', 0.15),
                            color: '#FF6F00',
                            fontWeight: 600,
                            borderColor: '#FF6F00',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={displayText}
                        sx={{
                          fontSize: { xs: '0.45rem', sm: '0.65rem' },
                          height: { xs: 18, sm: 22 },
                          bgcolor: alpha(color, 0.15),
                          color: color,
                          fontWeight: 600,
                          borderColor: color,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ 
                    fontSize: { xs: '0.55rem', sm: '0.75rem' }, 
                    fontWeight: 600,
                    color: isComplimentary ? '#7B1FA2' : isCancelled ? '#C62828' : isRefunded ? '#4A148C' : '#2E7D32',
                  }}>
                    {isComplimentary ? '₦0.00 (Comp.)' : formatCurrency(order.amount)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                    {order.waiter_name || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, color: 'text.secondary' }}>
                    {order.order_time ? formatTime(order.order_time) : 'N/A'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {hasMore && (
          <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#fafafa' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' } }}>
              + {orderHistory.length - 20} more orders
            </Typography>
          </Box>
        )}
      </TableContainer>
    );
  }, [reportData]);

  // ============================================
  // LOADING STATE
  // ============================================

  if (isLoading) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 2, sm: 3 },
            minHeight: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={50} sx={{ color: '#1976D2' }} />
          <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
            Generating EOD Report...
          </Typography>
        </Box>
      </Dialog>
    );
  }

  // ============================================
  // ERROR STATE
  // ============================================

  if (error) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        </DialogTitle>
        <DialogActions sx={{ pt: 2, px: 3, pb: 3 }}>
          <Button
            onClick={onClose}
            variant="contained"
            color="primary"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

 // ============================================
// MAIN RENDER - COMPLETE
// ============================================

const closure = reportData?.closure || reportData;
// ✅ FIX: Get orderHistory from reportData
const orderHistory = reportData?.order_history || [];

return (
  <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          bgcolor: alpha('#2E7D32', 0.05),
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: alpha('#2E7D32', 0.15),
              color: '#2E7D32',
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
            }}
          >
            <ReceiptLongIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              End of Day Report
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
              {locationName} • {closure?.closure_date ? formatDate(closure.closure_date) : 'N/A'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={isPrinting ? <CircularProgress size={18} /> : <PrintIcon />}
            onClick={handlePrint}
            disabled={isPrinting || !reportData}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.6rem', sm: '0.8rem' },
              borderColor: '#2E7D32',
              color: '#2E7D32',
              '&:hover': {
                borderColor: '#1B5E20',
                bgcolor: alpha('#2E7D32', 0.08),
              },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
            }}
          >
            {isPrinting ? 'Printing...' : 'Print Report'}
          </Button>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              bgcolor: alpha('#000', 0.05),
              '&:hover': { bgcolor: alpha('#000', 0.1) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflow: 'auto' }}>
        {reportData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            {/* Summary Cards */}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#1976D2', 0.15)}`,
                    bgcolor: alpha('#1976D2', 0.05),
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, display: 'block' }}
                  >
                    Total Orders
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, color: '#1976D2' }}
                  >
                    {closure?.total_orders || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#2E7D32', 0.15)}`,
                    bgcolor: alpha('#2E7D32', 0.05),
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, display: 'block' }}
                  >
                    Total Revenue
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, color: '#2E7D32' }}
                  >
                    {formatCompactCurrency(closure?.total_revenue || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#C62828', 0.15)}`,
                    bgcolor: alpha('#C62828', 0.05),
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, display: 'block' }}
                  >
                    Cashier
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.85rem' }, color: '#C62828' }}
                  >
                    {closure?.closed_by_cashier_name || 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2.5 },
                    borderRadius: '12px',
                    border: `1px solid ${alpha('#FF6F00', 0.15)}`,
                    bgcolor: alpha('#FF6F00', 0.05),
                    textAlign: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.5rem', sm: '0.7rem' }, display: 'block' }}
                  >
                    Closure Date
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.8rem' }, color: '#FF6F00' }}
                  >
                    {closure?.closure_date ? formatDate(closure.closure_date) : 'N/A'}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Payment Methods */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                💳 Payment Methods Breakdown
              </Typography>
              {renderPaymentMethods()}
            </Box>

            {/* Discounts Section */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                🏷️ Discounts
              </Typography>
              {renderDiscounts()}
            </Box>

            {/* Complimentary Section */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                🎁 Complimentary Orders
              </Typography>
              {renderComplimentary()}
            </Box>

            {/* Waiter Performance */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                👨‍🍳 Waiter Performance
              </Typography>
              {renderWaiterPerformance()}
            </Box>

            {/* ✅ Order History - Now displays all orders with statuses */}
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}
              >
                📋 Order History ({orderHistory.length} orders)
              </Typography>
              {renderOrderHistory()}
            </Box>

            {/* Footer Note */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.5, sm: 2 },
                borderRadius: '12px',
                bgcolor: alpha('#2E7D32', 0.05),
                border: `1px solid ${alpha('#2E7D32', 0.1)}`,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, display: 'block' }}
              >
                ✅ This report confirms the end of day closure for {locationName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, display: 'block', mt: 0.5 }}
              >
                Generated on {new Date().toLocaleString()}
              </Typography>
              {closure?.notes && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.5rem', sm: '0.65rem' }, display: 'block', mt: 0.5, fontStyle: 'italic' }}
                >
                  Note: {closure.notes}
                </Typography>
              )}
              {closure?.is_auto_closed && (
                <Chip
                  size="small"
                  label="Auto-closed day"
                  color="warning"
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No report data available</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          pb: { xs: 1.5, sm: 2 },
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={isPrinting || !reportData}
          startIcon={isPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
          sx={{
            bgcolor: '#2E7D32',
            '&:hover': { bgcolor: '#1B5E20' },
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            px: { xs: 3, sm: 4 },
            py: { xs: 1, sm: 1.25 },
            fontSize: { xs: '0.7rem', sm: '0.95rem' },
          }}
        >
          {isPrinting ? 'Printing...' : 'Print Report'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Fade}
    >
      <Alert
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%', borderRadius: '10px' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </>
);
};

export default EODReport;